import { z } from "zod";
import { bookListWithStatusQuerySchema } from "./reading";

/*
 * Ratings contract shared by server and UI: input schema (issue messages are field error codes
 * translated by the client), the `myRating` catalog filter and the DTOs.
 */

export const RATING_REVIEW_MAX = 10_000;
export const RATING_MIN_STARS = 1;
export const RATING_MAX_STARS = 5;

/** `PUT /api/books/:bookId/rating`. The review is trimmed; an empty review becomes `null`. */
export const ratingInputSchema = z.object({
  stars: z
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(RATING_MIN_STARS, { error: "out_of_range" })
    .max(RATING_MAX_STARS, { error: "out_of_range" }),
  review: z
    .string({ error: "invalid" })
    .trim()
    .max(RATING_REVIEW_MAX, { error: "too_long" })
    .transform((value) => (value === "" ? null : value))
    .nullish()
    .transform((value) => value ?? null),
});
export type RatingInput = z.input<typeof ratingInputSchema>;

export const MY_RATING_FILTERS = ["1", "2", "3", "4", "5", "rated", "unrated"] as const;
export type MyRatingFilter = (typeof MY_RATING_FILTERS)[number];

/** Query of `GET /api/books` including the reading-status and rating extensions. */
export const bookListFullQuerySchema = bookListWithStatusQuerySchema.extend({
  myRating: z.enum(MY_RATING_FILTERS, { error: "invalid" }).optional(),
});
export type BookListFullQuery = z.infer<typeof bookListFullQuerySchema>;

export interface RatingDto {
  stars: number;
  review: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BookRatingEntryDto {
  userId: string;
  userName: string;
  stars: number;
  review: string | null;
  updatedAt: string;
}

export interface BookRatingsDto {
  /** Average rounded to one decimal; `null` without ratings. */
  average: number | null;
  count: number;
  mine: RatingDto | null;
  /** Set when an admin removed the requesting user's rating; never exposed for other users. */
  myRemoval: { removedAt: string } | null;
  /** Own rating first, then newest change first. */
  ratings: BookRatingEntryDto[];
}
